import { apiRequest } from "@/lib/queryClient";

// POST /api/customer/lookup for the sign-in card and the kiosk.
//
// One phone can reach more than one account: two standalone accounts on one
// number, a group's locations, an authorized contact. The server then answers
// with a list ({ multipleAccounts, accounts }) instead of one account; the card
// shows a picker (names only) and asks again with the buyer's choice. The
// server checks that choice against the options for that phone, so the second
// answer is the ordinary one-account shape the rest of the card already uses.

export interface LookupAccountOption {
  accountId: number;
  name: string;
  authType: "direct" | "group_child" | "authorized_contact";
  contactId?: number;
}

export type LookupResult<Account> =
  | { kind: "account"; account: Account }
  | { kind: "choose"; options: LookupAccountOption[] }
  | { kind: "not-found" }
  | { kind: "rate-limited" };

const AUTH_TYPES: ReadonlyArray<LookupAccountOption["authType"]> = ["direct", "group_child", "authorized_contact"];

// Keeps only what the picker needs from each option, so nothing else the
// response may carry is ever rendered or sent back.
export function pickerOptionsOf(body: unknown): LookupAccountOption[] {
  const accounts = (body as { accounts?: unknown })?.accounts;
  if (!Array.isArray(accounts)) return [];
  return accounts.flatMap((raw): LookupAccountOption[] => {
    const o = raw as Record<string, unknown>;
    if (typeof o?.accountId !== "number" || typeof o.name !== "string") return [];
    const authType = AUTH_TYPES.find((t) => t === o.authType);
    if (!authType) return [];
    return [{
      accountId: o.accountId,
      name: o.name,
      authType,
      ...(authType === "authorized_contact" && typeof o.contactId === "number" ? { contactId: o.contactId } : {}),
    }];
  });
}

// The body for a lookup of `phoneDigits`, narrowed to one picked option.
export function lookupBody(phoneDigits: string, choice?: LookupAccountOption) {
  if (!choice) return { phone: phoneDigits };
  return {
    phone: phoneDigits,
    accountId: choice.accountId,
    ...(choice.contactId !== undefined ? { contactId: choice.contactId } : {}),
  };
}

// The group location to sign straight into after the parent's PIN, when that
// is what the buyer picked. Undefined for every other option.
export function chosenChildAccountId(choice: LookupAccountOption | null): number | undefined {
  return choice?.authType === "group_child" ? choice.accountId : undefined;
}

export async function lookupCustomer<Account>(
  phoneDigits: string,
  choice?: LookupAccountOption,
): Promise<LookupResult<Account>> {
  try {
    const response = await apiRequest("POST", "/api/customer/lookup", lookupBody(phoneDigits, choice));
    const body = await response.json();
    if (body?.multipleAccounts) {
      const options = pickerOptionsOf(body);
      // A choice is answered with one account, never another list.
      if (choice || options.length === 0) return { kind: "not-found" };
      return { kind: "choose", options };
    }
    return { kind: "account", account: body as Account };
  } catch (error) {
    // apiRequest throws "<status>: <body>" on any non-2xx.
    const status = /^(\d{3}):/.exec(String((error as Error)?.message ?? ""))?.[1];
    if (status === "404") return { kind: "not-found" };
    if (status === "429") return { kind: "rate-limited" };
    throw error;
  }
}
